import {
    Card,
    DATE_FORMATS,
    DateInput,
    Heading,
    Icon,
    RadioGroup,
    Text,
    TextInput,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    type RadioGroupOption,
} from '@amelio/ui-library';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';

import { FormField } from '@/components/FormField/FormField';
import { Locale } from '@/constants/Locale';
import { ReviewScheduleType } from '@/constants/review/ReviewScheduleType';
import { useAmelioTranslations } from '@/hooks';
import i18n from '@/plugins/i18n';

const MIN_DAYS = 1;
const DEFAULT_DAYS = 14;

interface Props {
    options?: RadioGroupOption[];
    schedulingOption?: ReviewScheduleType;
    daysToComplete?: number;
    scheduledAt: Date;
    onSchedulingOptionChange?: (value: string) => void;
    onDaysToCompleteChange?: (value: number) => void;
    onScheduledAtChange: (date: Date) => void;
    hasSelfAssessment?: boolean;
}

export const ScheduleCompletion = ({
    options,
    schedulingOption,
    daysToComplete = DEFAULT_DAYS,
    scheduledAt,
    onSchedulingOptionChange,
    onDaysToCompleteChange,
    onScheduledAtChange,
    hasSelfAssessment = false,
}: Props) => {
    const { t } = useAmelioTranslations();
    const [inputValue, setInputValue] = useState(String(daysToComplete));

    useEffect(() => {
        setInputValue(String(daysToComplete));
    }, [daysToComplete]);

    const handleDaysChange = (newValue: string) => {
        setInputValue(newValue);

        const numValue = Number(newValue);
        if (!isNaN(numValue) && numValue >= MIN_DAYS) {
            onDaysToCompleteChange?.(numValue);
        }
    };

    const handleDaysBlur = () => {
        const numValue = Number(inputValue);
        if (isNaN(numValue) || numValue < MIN_DAYS || inputValue === '') {
            setInputValue(String(MIN_DAYS));
            onDaysToCompleteChange?.(MIN_DAYS);
        }
    };

    return (
        <Card
            className={'mt-2'}
            bgColor={'bg-white'}
            borderRadius={'2xl'}
            padding={'xl'}
        >
            <div className={'flex items-center'}>
                <Icon
                    name={'edit-menu-board'}
                    size={'xl'}
                    className={'mr-3'}
                />

                <Heading level={5}>
                    {t('evaluation360ScheduleCreation.scheduleOptions.title')}
                </Heading>
            </div>

            <div className={'my-5 flex flex-col gap-2 md:ml-10'}>
                <RadioGroup
                    options={options ?? []}
                    name={'schedule'}
                    value={schedulingOption}
                    onChange={onSchedulingOptionChange}
                    dataTestId={'radio-group-sm'}
                />
            </div>

            <AnimatePresence>
                {schedulingOption === ReviewScheduleType.SCHEDULE_FOR_LATER && (
                    <motion.div
                        className={'max-w-80'}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        <DateInput
                            placeholder={t('common.selectDate')}
                            onChange={(value) => onScheduledAtChange(value ?? new Date())}
                            value={scheduledAt}
                            lang={i18n.language === Locale.EN ? 'en' : 'fr'}
                            dateFormat={DATE_FORMATS.DISPLAY_DATE_TIME}
                            showTime
                            disabledDates={{
                                before: new Date(),
                            }}
                            clearable={false}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            <div className={'mt-5'}>
                <Text>
                    {hasSelfAssessment
                        ? t(
                              'evaluation360ScheduleCreation.scheduleOptions.daysAllowedSelfAssessment',
                          )
                        : t('evaluation360ScheduleCreation.scheduleOptions.daysAllowed')}
                </Text>

                <div className={'flex flex-col items-start gap-2 md:flex-row md:items-center'}>
                    <FormField label={''}>
                        <TextInput
                            type={'number'}
                            className={'w-20'}
                            placeholder={String(DEFAULT_DAYS)}
                            value={inputValue}
                            onChange={handleDaysChange}
                            onBlur={handleDaysBlur}
                        />
                    </FormField>

                    <div className={'flex flex-wrap items-start gap-2 md:items-center'}>
                        <Text> {t('evaluation360ScheduleCreation.scheduleOptions.days')}</Text>

                        {/* Tooltip for days allowed */}
                        <TooltipProvider>
                            <Tooltip delayDuration={200}>
                                <TooltipTrigger asChild={true}>
                                    <Icon name={'essential-info-circle'} />
                                </TooltipTrigger>

                                <TooltipContent side={'bottom'}>
                                    {t('evaluation360ScheduleCreation.scheduleOptions.recommended')}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>

                        <Text className={'tex'}>
                            {t('evaluation360ScheduleCreation.scheduleOptions.recommended')}
                        </Text>
                    </div>
                </div>
            </div>
        </Card>
    );
};
